import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

/**
 * Logout page component that handles the logout process
 * and redirects to the login page
 */
const LogoutPage: React.FC = () => {
  const auth = useAuth();
  const location = useLocation();
  const [logoutCompleted, setLogoutCompleted] = useState(false);
  const [logStatus, setLogStatus] = useState<string[]>([]);
  
  useEffect(() => {
    // Log for debugging
    console.log('LogoutPage - Processing logout');
    setLogStatus(prev => [...prev, 'Starting logout process']);
    
    // Check special parameters
    const queryParams = new URLSearchParams(location.search);
    const hasTimestamp = queryParams.has('t');
    const isLocalLogout = queryParams.has('local');
    const skipCognito = queryParams.has('skip_cognito');
    const fromOidc = queryParams.has('from') && queryParams.get('from') === 'oidc';
    const fromCognitoLogout = queryParams.has('from') && queryParams.get('from') === 'cognito_logout';
    const isCompleteLogout = queryParams.has('complete');
    
    // Check logout mode
    if (hasTimestamp) {
      setLogStatus(prev => [...prev, `Logout with timestamp: ${queryParams.get('t')}`]);
    }
    
    if (isLocalLogout) {
      setLogStatus(prev => [...prev, 'Local logout mode (bypassing Cognito)']);
    }
    
    if (skipCognito) {
      setLogStatus(prev => [...prev, 'Cognito logout intentionally skipped']);
    }
    
    if (fromOidc) {
      setLogStatus(prev => [...prev, 'Redirect from OIDC callback']);
    }
    
    if (fromCognitoLogout) {
      setLogStatus(prev => [...prev, 'Redirect after Cognito logout']);
    }
    
    if (isCompleteLogout) {
      setLogStatus(prev => [...prev, 'Complete logout (including Cognito) finished']);
    }

    const performLogout = async () => {
      try {
        // 1. Log initial state for debugging
        const initialState = {
          hasAccessToken: !!localStorage.getItem('access_token'),
          oidcKeys: Object.keys(localStorage).filter(key => key.startsWith('oidc.user:')),
          isAuthenticated: auth.isAuthenticated,
          hasUser: !!auth.user
        };
        console.log('State before logout:', initialState);
        setLogStatus(prev => [...prev, 'State before logout logged']);
        
        // 2. Clear authentication-related cookies
        // Important for incognito tabs
        setLogStatus(prev => [...prev, 'Clearing authentication cookies']);
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          if (name && (
             name.includes('cognito') || 
             name.includes('auth') || 
             name.includes('id') || 
             name.includes('token')
          )) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            console.log(`Cookie removed: ${name}`);
            setLogStatus(prev => [...prev, `Cookie removed: ${name}`]);
          }
        });
        
        // 3. Try the official OIDC library logout only if we're not in skip mode
        // Only if we're not in local logout mode or if we shouldn't skip Cognito
        if (auth.isAuthenticated && !isLocalLogout && !skipCognito && !fromOidc) {
          try {
            // Clear local data first
            setLogStatus(prev => [...prev, 'Clearing local session before Cognito logout']);
            
            // Clear the user session first
            setLogStatus(prev => [...prev, 'Clearing OIDC session']);
            await auth.removeUser();
            
            // Manually construct the logout URL with correct parameters
            // AWS Cognito doesn't support id_token_hint, only client_id and logout_uri
            const cognitoDomain = 'https://auth.cerdata.co';
            const logoutUri = encodeURIComponent(window.location.origin + '/login');
            const clientId = '1m01p52tij9vis42i8jjjdji09';
            
            // Construct URL with only supported parameters
            const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
            
            setLogStatus(prev => [...prev, `Redirecting to Cognito logout: ${logoutUrl}`]);
            
            // Redirect to Cognito logout endpoint
            window.location.href = logoutUrl;
            return; // Stop execution here
          } catch (oidcError) {
            console.warn('Error attempting logout:', oidcError);
            setLogStatus(prev => [...prev, `Error in logout: ${oidcError}`]);
          }
        } else {
          // Log to explain why we're skipping signoutRedirect
          if (isLocalLogout || skipCognito) {
            setLogStatus(prev => [...prev, 'Skipping Cognito logout (local logout mode)']);
          } else if (fromOidc) {
            setLogStatus(prev => [...prev, 'Skipping Cognito logout (already came from OIDC)']);
          } else if (!auth.isAuthenticated) {
            setLogStatus(prev => [...prev, 'Skipping Cognito logout (user not authenticated)']);
          }
        }
        
        // 4. Remove local tokens
        setLogStatus(prev => [...prev, 'Removing local tokens']);
        localStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token');
        
        // 5. Clear ALL OIDC and auth keys from localStorage and sessionStorage
        const storages = [localStorage, sessionStorage];
        
        storages.forEach(storage => {
          // Clear OIDC and other authentication data
          const authKeys = Object.keys(storage).filter(key => 
            key.startsWith('oidc.') || 
            key.includes('oidc') || 
            key.includes('auth') ||
            key.includes('token') ||
            key.includes('cognito')
          );
          
          if (authKeys.length > 0) {
            setLogStatus(prev => [...prev, `Removing ${authKeys.length} keys from ${storage === localStorage ? 'localStorage' : 'sessionStorage'}`]);
            authKeys.forEach(key => storage.removeItem(key));
          }
        });
        
        // 6. Clear user from OIDC session
        if (auth.user) {
          setLogStatus(prev => [...prev, 'Removing user from OIDC session']);
          try {
            await auth.removeUser();
          } catch (removeError) {
            console.warn('Error removing user:', removeError);
            setLogStatus(prev => [...prev, `Error removing user: ${removeError}`]);
          }
        }
        
        // 7. Log final state to confirm cleanup
        const finalState = {
          hasAccessToken: !!localStorage.getItem('access_token'),
          oidcKeys: Object.keys(localStorage).filter(key => key.startsWith('oidc.user:')),
          isAuthenticated: auth.isAuthenticated,
          hasUser: !!auth.user
        };
        console.log('State after logout:', finalState);
        setLogStatus(prev => [...prev, 'State after logout logged']);
        
        // 8. Final redirect to login page
        setLogStatus(prev => [...prev, 'Redirecting to login...']);
        const timestamp = new Date().getTime();
        console.log('Logout completed, redirecting with timestamp:', timestamp);
        
        // Force complete page reload to clear any residual state
        setTimeout(() => {
          // Ensure redirect URL contains parameters to avoid cache
          window.location.href = `/login?t=${timestamp}&clear=1`;
        }, 800); // Small delay to ensure all async processes are completed
      } catch (error) {
        console.error('Error during logout process:', error);
        setLogStatus(prev => [...prev, `Fatal error in logout: ${error}`]);
        setLogoutCompleted(true); // Continue with React redirect in case of failure
      }
    };
    
    performLogout();
  }, [auth, location.search]);
  
  // This redirect will only be used if window.location redirect fails
  if (logoutCompleted) {
    return <Navigate to="/login" replace />;
  }
  
  // Display loading indicator while logout is being processed
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="flex flex-col items-center max-w-md w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
        <h2 className="text-lg font-medium text-gray-900 mb-2">Logging out...</h2>
        <p className="text-sm text-gray-500">Please wait while we securely log you out</p>
        
      </div>
    </div>
  );
};

export default LogoutPage;